/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */
import { cloneElement, FunctionComponent, VNode } from 'preact';
import {
  Children,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'preact/compat';

import '@/pdp/components/Zoom/Zoom.css';
import { Button } from '@adobe-commerce/elsie/components';
import { Close as CloseSVG } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface ZoomProps {
  children: VNode[] | VNode;
  scale?: number;
  closeButton?: boolean;
}

export const Zoom: FunctionComponent<ZoomProps> = ({
  children,
  scale = 2.0,
  closeButton = true,
}) => {
  const [zoomed, setZoomed] = useState(false);
  const [figureClassName, setFigureClassName] = useState('pdp-zoom');
  const [drag, setDrag] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLElement>(null);
  const cachedBoundsRef = useRef<DOMRect | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastTransformRef = useRef<string>('none');

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );
  const isSwipingRef = useRef<boolean>(false);
  const lastValidTouchRef = useRef<{ clientX: number; clientY: number } | null>(
    null
  );

  const updatePosition = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const updatePositionLogic = () => {
        let zoomer: DOMRect;

        if (cachedBoundsRef.current && containerRef.current) {
          zoomer = cachedBoundsRef.current;
        } else if (containerRef.current) {
          zoomer = containerRef.current.getBoundingClientRect();
          cachedBoundsRef.current = zoomer;
        } else {
          zoomer = (
            event.currentTarget as HTMLElement
          )?.getBoundingClientRect();
        }

        const overlap = (100 - 100 / scale) / 2;
        let coordinates: { clientX: number; clientY: number } | null = null;

        if ('touches' in event) {
          if (event.touches.length > 0) {
            coordinates = event.touches[0];
            lastValidTouchRef.current = {
              clientX: coordinates.clientX,
              clientY: coordinates.clientY,
            };
          } else if (event.type === 'touchend' && touchStartRef.current) {
            coordinates = {
              clientX: touchStartRef.current.x,
              clientY: touchStartRef.current.y,
            };
          } else if (lastValidTouchRef.current) {
            coordinates = lastValidTouchRef.current;
          } else {
            return;
          }
        } else if (event instanceof MouseEvent) {
          coordinates = event;
        } else {
          return;
        }

        const relativeX =
          ((coordinates.clientX - zoomer.left) / zoomer.width) * 100;
        const relativeY =
          ((coordinates.clientY - zoomer.top) / zoomer.height) * 100;

        const x = ((50 - relativeX) * (scale - 1)) / scale;
        const y = ((50 - relativeY) * (scale - 1)) / scale;

        setPosition({
          x: Math.max(-overlap, Math.min(x, overlap)) || 0,
          y: Math.max(-overlap, Math.min(y, overlap)) || 0,
        });
      };

      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
        updatePositionLogic();
      } else {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(updatePositionLogic);
      }
    },
    [scale]
  );

  useEffect(() => {
    cachedBoundsRef.current = null;
  }, [zoomed]);

  const startDrag = (event: TouchEvent) => {
    if (!zoomed || event.type !== 'touchstart') return;
    setDragStart({ x: event.touches[0].clientX, y: event.touches[0].clientY });
    setDragStartPosition({ x: position.x, y: position.y });
    setDrag(true);
  };

  const dragMove = (event: TouchEvent) => {
    if (!drag || event.type !== 'touchmove') return;
    const zoomer = (
      event.currentTarget as HTMLElement
    )?.getBoundingClientRect();
    const overlap = (100 - 100 / scale) / 2;
    const movedX = event.touches[0].clientX - dragStart.x;
    const movedY = event.touches[0].clientY - dragStart.y;
    const movedXPercent = (movedX * 100) / (zoomer.width * scale);
    const movedYPercent = (movedY * 100) / (zoomer.height * scale);
    const x = dragStartPosition.x + movedXPercent;
    const y = dragStartPosition.y + movedYPercent;
    setPosition({
      x: Math.max(-overlap, Math.min(x, overlap)) || 0,
      y: Math.max(-overlap, Math.min(y, overlap)) || 0,
    });
    event.preventDefault();
  };

  const handleTouchStart = (event: TouchEvent) => {
    if (closeButton) {
      touchStartRef.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        time: Date.now(),
      };

      lastValidTouchRef.current = {
        clientX: event.touches[0].clientX,
        clientY: event.touches[0].clientY,
      };

      isSwipingRef.current = false;

      if (zoomed) {
        startDrag(event);
      }
    } else {
      if (drag) return;

      !zoomed && updatePosition(event);

      setZoomed(!zoomed);

      event.preventDefault();
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (event.touches.length === 0) return;

    lastValidTouchRef.current = {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    };

    if (closeButton) {
      if (!touchStartRef.current) return;

      if (drag) {
        dragMove(event);
        return;
      }

      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      const deltaX = Math.abs(touchX - touchStartRef.current.x);
      const deltaY = Math.abs(touchY - touchStartRef.current.y);

      if (deltaX > 10 || deltaY > 10) {
        isSwipingRef.current = true;
      }
    }

    if (zoomed) {
      updatePosition(event);
    }
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (closeButton) {
      if (!touchStartRef.current) return;

      const tapDuration = Date.now() - touchStartRef.current.time;

      if (!isSwipingRef.current && tapDuration < 300 && !drag) {
        setZoomed(true);
        updatePosition(event);
        event.preventDefault();
      }

      setDrag(false);
      touchStartRef.current = null;
    } else {
      setZoomed(false);
    }
  };

  const handleClick = (event: MouseEvent) => {
    if ('touches' in event) return;

    if (drag) return;

    if (zoomed) {
      !closeButton && setZoomed(false);
    } else {
      setZoomed(true);
      updatePosition(event);
    }
    event.preventDefault();
  };

  const handleMove = (event: MouseEvent) => {
    if ('touches' in event) return;

    if (zoomed) {
      updatePosition(event);
    }
  };

  const handleLeave = (event: MouseEvent) => {
    if ('touches' in event) return;

    if (closeButton) {
      setDrag(false);
      return;
    }
    setZoomed(false);
  };

  const closeButtonClick = (event: MouseEvent | TouchEvent) => {
    setZoomed(false);
    event.stopPropagation();
  };

  const transformStyle = zoomed
    ? `scale(${scale}) translateX(${position.x}%) translateY(${position.y}%)`
    : 'none';

  const CloseLabel = useText('PDP.Zoom.Close.label').label;
  const child = Children.only(children) as any;

  useEffect(() => {
    const handleGalleryChange = () => {
      if (zoomed) {
        setZoomed(false);
        setPosition({ x: 0, y: 0 });
        setDrag(false);
      }
    };

    const gallery = document.querySelector('#gallery');
    if (gallery) {
      gallery.addEventListener('click', handleGalleryChange);
      return () => gallery.removeEventListener('click', handleGalleryChange);
    }
  }, [zoomed]);

  const ImageClone = cloneElement(child, {
    ...child.props,
    draggable: false,
    style: {
      ...child.props?.style,
      '--transform': transformStyle,
    },
  });

  useEffect(() => {
    const className = zoomed
      ? `pdp-zoom ${closeButton ? 'pdp-zoom--no-effects' : 'pdp-zoom--zoomed'}`
      : 'pdp-zoom';
    setFigureClassName(className);
  }, [zoomed, closeButton]);

  useEffect(() => {
    if (lastTransformRef.current === transformStyle) return;
    lastTransformRef.current = transformStyle;

    const updateTransforms = () => {
      if (!containerRef.current) return;

      const images = containerRef.current.querySelectorAll('img');
      images.forEach((img) => {
        if (zoomed && transformStyle !== 'none') {
          img.style.setProperty('transform', transformStyle, 'important');
        } else {
          img.style.removeProperty('transform');
        }
      });
    };

    requestAnimationFrame(updateTransforms);
  }, [transformStyle, zoomed]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <figure
      ref={containerRef}
      className={figureClassName}
      onClick={handleClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {ImageClone}
      {closeButton && zoomed && (
        <Button
          aria-label={CloseLabel}
          variant="tertiary"
          className="pdp-zoom__close-button"
          icon={<CloseSVG />}
          onClick={closeButtonClick}
        />
      )}
    </figure>
  );
};
